// Growth endpoints: /api/v1/admin/platform/growth/* (API_CONTRACT.md › Growth;
// backend/src/routes/admin/platform-growth.ts). Platform data, not tenant data.

import { apiFetch } from './client'
import type { FinderRun } from './growthInsights'
import type {
  BulkStatusResult,
  ConvertInquiryResponse,
  Inquiry,
  InquiryListParams,
  InquiryListResponse,
  InquiryStatus,
  Lead,
  LeadActivity,
  LeadImportReport,
  LeadImportRow,
  LeadListParams,
  LeadListResponse,
  LeadReviewResponse,
  LeadStatus,
  LeadWrite,
  Preview,
  PreviewCreateBody,
  PreviewListParams,
  PreviewListResponse,
  PreviewReport,
  PreviewConvertResponse,
  ProvisioningResult,
  Suppression,
  VerifyDecisionStatus,
} from './types'

const BASE = '/admin/platform/growth'

// ─── Leads ──────────────────────────────────────────────────────────────────

export function listLeads(params: LeadListParams = {}, signal?: AbortSignal): Promise<LeadListResponse> {
  return apiFetch(`${BASE}/leads`, { query: { ...params, q: params.q?.trim() || undefined }, signal })
}

export async function getLead(id: number, signal?: AbortSignal): Promise<Lead> {
  const r = await apiFetch<{ lead: Lead }>(`${BASE}/leads/${id}`, { signal })
  return r.lead
}

/** 409 duplicate_lead names the lead that has this email or website in `details.lead_id`. */
export async function createLead(body: LeadWrite): Promise<Lead> {
  const r = await apiFetch<{ lead: Lead }>(`${BASE}/leads`, { method: 'POST', body })
  return r.lead
}

/** 409 do_not_contact: nothing moves a lead out of that status. */
export async function updateLead(id: number, patch: LeadWrite): Promise<Lead> {
  const r = await apiFetch<{ lead: Lead }>(`${BASE}/leads/${id}`, { method: 'PATCH', body: patch })
  return r.lead
}

/** 409 do_not_contact: that lead stays. */
export function deleteLead(id: number): Promise<void> {
  return apiFetch(`${BASE}/leads/${id}`, { method: 'DELETE' })
}

export function bulkLeadStatus(ids: number[], status: LeadStatus): Promise<BulkStatusResult> {
  return apiFetch(`${BASE}/leads/bulk-status`, { method: 'POST', body: { ids, status } })
}

/** 202: reads each lead's pages in the background, as a finder run. 409 finder_busy while another run is going. */
export async function checkLeadActivity(ids: number[]): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${BASE}/leads/check-activity`, { method: 'POST', body: { ids } })
  return r.run
}

/**
 * 202: where each lead lives today, read in the background as a finder run. 409 finder_busy while another run is going.
 * A lead a person decided by hand is left as it is, unless `replaceDecisions` (Verify again on its page).
 */
export async function verifyLeads(ids: number[], opts: { replaceDecisions?: boolean } = {}): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${BASE}/leads/verify`, { method: 'POST', body: { ids, ...(opts.replaceDecisions ? { replace_decisions: true } : {}) } })
  return r.run
}

/** The For review card: needs_review leads, best first, and how many aren't checked yet. */
export function reviewLeads(limit: number, signal?: AbortSignal): Promise<LeadReviewResponse> {
  return apiFetch(`${BASE}/leads/review`, { query: { limit }, signal })
}

/** A person's call: verified or failed, or needs_review (Back to review). 409 do_not_contact. */
export async function decideLeadVerification(id: number, status: VerifyDecisionStatus, note?: string): Promise<Lead> {
  const r = await apiFetch<{ lead: Lead }>(`${BASE}/leads/${id}/verification`, { method: 'POST', body: { status, ...(note ? { note } : {}) } })
  return r.lead
}

/** dry_run is the preview: the same pass with no writes. `verify` checks what was added, in the background. */
export function importLeads(body: { rows: LeadImportRow[]; skip_without_contact?: boolean; dry_run?: boolean; verify?: boolean }): Promise<LeadImportReport> {
  return apiFetch(`${BASE}/leads/import`, { method: 'POST', body })
}

export async function leadActivity(id: number, signal?: AbortSignal): Promise<LeadActivity[]> {
  const r = await apiFetch<{ activity: LeadActivity[] }>(`${BASE}/leads/${id}/activity`, { signal })
  return r.activity
}

export function addLeadNote(id: number, body: string): Promise<{ activity: LeadActivity }> {
  return apiFetch(`${BASE}/leads/${id}/notes`, { method: 'POST', body: { body } })
}

// ─── Suppressions ───────────────────────────────────────────────────────────

export function addSuppression(body: { email: string; note?: string }): Promise<{ suppression: Suppression; created: boolean; lead_ids: number[] }> {
  return apiFetch(`${BASE}/suppressions`, { method: 'POST', body })
}

// ─── Inquiries ──────────────────────────────────────────────────────────────

export function listInquiries(params: InquiryListParams = {}, signal?: AbortSignal): Promise<InquiryListResponse> {
  return apiFetch(`${BASE}/inquiries`, { query: { ...params, q: params.q?.trim() || undefined }, signal })
}

export async function getInquiry(id: number, signal?: AbortSignal): Promise<Inquiry> {
  const r = await apiFetch<{ inquiry: Inquiry }>(`${BASE}/inquiries/${id}`, { signal })
  return r.inquiry
}

export async function updateInquiry(id: number, patch: { status?: InquiryStatus; notes?: string | null }): Promise<Inquiry> {
  const r = await apiFetch<{ inquiry: Inquiry }>(`${BASE}/inquiries/${id}`, { method: 'PATCH', body: patch })
  return r.inquiry
}

export function convertInquiry(id: number): Promise<ConvertInquiryResponse> {
  return apiFetch(`${BASE}/inquiries/${id}/convert`, { method: 'POST' })
}

// ─── Previews ───────────────────────────────────────────────────────────────

export function listPreviews(params: PreviewListParams = {}, signal?: AbortSignal): Promise<PreviewListResponse> {
  return apiFetch(`${BASE}/previews`, { query: { ...params, q: params.q?.trim() || undefined }, signal })
}

export async function getPreview(id: number, signal?: AbortSignal): Promise<Preview> {
  const r = await apiFetch<{ preview: Preview }>(`${BASE}/previews/${id}`, { signal })
  return r.preview
}

/** What the save would do, with no writes (dry_run). */
export async function checkPreview(body: PreviewCreateBody): Promise<PreviewReport> {
  const r = await apiFetch<{ report: PreviewReport }>(`${BASE}/previews`, { method: 'POST', body: { ...body, dry_run: true } })
  return r.report
}

/** `provisioning` is the Cloudflare result for its address, as for a new tenant. */
export function createPreview(body: PreviewCreateBody): Promise<{ preview: Preview; report: PreviewReport; provisioning?: ProvisioningResult }> {
  return apiFetch(`${BASE}/previews`, { method: 'POST', body })
}

async function previewAction(id: number, action: string, body?: Record<string, unknown>): Promise<Preview> {
  const r = await apiFetch<{ preview: Preview }>(`${BASE}/previews/${id}/${action}`, { method: 'POST', body })
  return r.preview
}

export const extendPreview = (id: number, days: number) => previewAction(id, 'extend', { days })
/** The site and its league go now; the row stays as the lead's history. */
export const deletePreviewNow = (id: number) => previewAction(id, 'delete-now')

/** A real league with a normal trial (14 days by default); the lead's account and changes stay. */
export function convertPreview(id: number, body: { trial_days?: number } = {}): Promise<PreviewConvertResponse> {
  return apiFetch(`${BASE}/previews/${id}/convert`, { method: 'POST', body })
}

/** Ops looks at the site as a super admin: a 15-minute impersonation link that lands on the site. */
export function openPreview(id: number): Promise<{ url: string; expires_at: string }> {
  return apiFetch(`${BASE}/previews/${id}/open`, { method: 'POST' })
}
